// hooks, lib
import { observer } from 'mobx-react-lite';
import { useFirstStageVm } from './useFirstStage.vm';

// components
import { Input } from '../../../../../../shared/ui/form/input/Input';
import { Label } from '../../../../../../shared/ui/form/label/Label';
import { CheckboxesGroup } from '../../../../../../shared/ui/form/checkboxesGroup/CheckboxesGroup';
import { Button, FormControlLabel, Grid, Radio, Typography } from '@mui/material';
import { ButtonsActions } from '../../common/buttonsActions/ButtonsActions';
import { MultipleSelect } from '../../../../../../shared/ui/form/multipleSelect/MultipleSelect';

// helpes, utils
import {
  carbohydrateMetabolismDisordersCheckboxesConfig,
  complicationsOfHypercortisolismOptions,
  physicalExaminationsCheckboxesConfig,
  psychiatricDisordersCheckboxesConfig,
  symptonsOptions,
  thromboembolicComplicationsCheckboxesConfig
} from './FirstStage.helper';
import { checkEmptyNumber } from '../../../CaseDraft.helper';

// icons
import { Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

// styles
import { RadioGroupStyled } from '../../common/fourthStage/FourthStage.styled';
import { LabelRadioGroup } from '../../../CaseDraft.styled';
import { useParams } from 'react-router-dom';

export const FirstStage = observer(() => {
  const { handleChangeArray, formik, handleSave, handleDraftSave, setOption, addComorbidity, handleDeleteComorbidity } =
    useFirstStageVm();
  const params = useParams();

  const isCushing = params && params?.type?.includes('cushing') && params?.version?.includes('v2');
  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container justifyContent={'space-between'} maxWidth="100%">
        <Grid xs={12} item>
          <Typography variant={'h6'} mt={2}>
            Objawy
          </Typography>
          <MultipleSelect
            label={'Objawy'}
            elementName="symptoms"
            selectedValues={formik.values.symptoms}
            handleChange={values => setOption(values as string[], 'symptoms')}
            options={symptonsOptions}
          />
        </Grid>
        {formik.values.symptoms?.includes('zaburzenia psychiatryczne') && (
          <>
            <Grid xs={12} item mt={2}>
              <Label>*Zaburzenia psychiatryczne:</Label>
            </Grid>
            <Grid item xs={12} mt={1}>
              <CheckboxesGroup
                onError={formik.touched.psychiatricDisorders && Boolean(formik.errors.psychiatricDisorders)}
                name={'psychiatricDisorders'}
                elements={psychiatricDisordersCheckboxesConfig}
                onChange={handleChangeArray}
                values={formik.values.psychiatricDisorders}
              />
            </Grid>
          </>
        )}
        <Grid container display={'flex'} alignItems={'center'}>
          <Grid item xs={12} mb={1}>
            <Typography variant={'h6'} mt={4}>
              Badanie przedmiotowe
            </Typography>
          </Grid>

          <Grid item xs={2.5} pr={1}>
            <Input
              InputLabelProps={{ shrink: true }}
              fullWidth={true}
              required={true}
              name={'weight'}
              label={'Masa ciała (kg)'}
              type={'number'}
              maxLength={3}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={checkEmptyNumber(formik.values.weight)}
              error={formik.touched.weight && Boolean(formik.errors.weight)}
            />
          </Grid>
          <Grid item xs={2.5} pr={1}>
            <Input
              InputLabelProps={{ shrink: true }}
              fullWidth={true}
              required={true}
              name={'height'}
              label={'Wzrost (cm)'}
              type={'number'}
              maxLength={2}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={checkEmptyNumber(formik.values.height)}
              error={formik.touched.height && Boolean(formik.errors.height)}
            />
          </Grid>
          <Grid item xs={2} pr={1}>
            <Input
              InputLabelProps={{ shrink: true }}
              fullWidth={true}
              readOnly={true}
              name={'bmi'}
              label={'BMI'}
              type={'text'}
              value={checkEmptyNumber(formik.values.bmi)}
            />
          </Grid>
          <Grid item xs={2.5} pr={1}>
            <Input
              InputLabelProps={{ shrink: true }}
              fullWidth={true}
              required={true}
              name={'bloodPressure'}
              label={'Ciśnienie tętnicze'}
              type={'text'}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.bloodPressure}
              error={formik.touched.bloodPressure && Boolean(formik.errors.bloodPressure)}
            />
          </Grid>
          <Grid item xs={2}>
            <Input
              InputLabelProps={{ shrink: true }}
              fullWidth={true}
              required={true}
              name={'heartRate'}
              label={'Tętno'}
              type={'number'}
              maxLength={3}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={checkEmptyNumber(formik.values.heartRate)}
              error={formik.touched.heartRate && Boolean(formik.errors.heartRate)}
            />
          </Grid>
        </Grid>
        <Grid item xs={6} pl={1} display={'flex'} mt={2}>
          <RadioGroupStyled row={true} name={'gender'} value={formik.values.gender}>
            <LabelRadioGroup error={Boolean(formik.errors.gender)}>Płeć:</LabelRadioGroup>
            <FormControlLabel
              onChange={formik.handleChange}
              value={'F'}
              control={<Radio color={Boolean(formik.errors.gender) ? 'error' : 'primary'} />}
              label="Kobieta"
            />
            <FormControlLabel
              onChange={formik.handleChange}
              value={'M'}
              control={<Radio color={Boolean(formik.errors.gender) ? 'error' : 'primary'} />}
              label="Mężczyzna"
            />
            <FormControlLabel
              onChange={formik.handleChange}
              value={'O'}
              control={<Radio color={Boolean(formik.errors.gender) ? 'error' : 'primary'} />}
              label="Inna"
            />
          </RadioGroupStyled>
        </Grid>

        <Grid item xs={12} mt={2}>
          <Label>Badania przedmiotowe</Label>
        </Grid>
        <Grid item xs={12} mt={1}>
          <CheckboxesGroup
            name={'physicalExaminations'}
            elements={physicalExaminationsCheckboxesConfig}
            onChange={handleChangeArray}
            values={formik.values.physicalExaminations}
          />
        </Grid>
        <Grid xs={12} item mt={4}>
          <MultipleSelect
            label={'Powikłania hiperkortyzolemii'}
            elementName="complicationsOfHypercortisolism"
            selectedValues={formik.values.complicationsOfHypercortisolism}
            isError={Boolean(formik.errors.complicationsOfHypercortisolism)}
            handleChange={values => setOption(values as string[], 'complicationsOfHypercortisolism')}
            options={complicationsOfHypercortisolismOptions}
          />
        </Grid>
        {formik.values.complicationsOfHypercortisolism?.includes('powikłania zakrzepowo-zatorowe') && (
          <>
            <Grid xs={12} item mt={2}>
              <Label>*Opis powikłań zakrzepowo-zatorowych:</Label>
            </Grid>
            <Grid item xs={12} mt={1}>
              <CheckboxesGroup
                onError={Boolean(formik.errors.thromboembolicComplications)}
                name={'thromboembolicComplications'}
                elements={thromboembolicComplicationsCheckboxesConfig}
                onChange={handleChangeArray}
                values={formik.values.thromboembolicComplications}
              />
            </Grid>
          </>
        )}
        {formik.values.complicationsOfHypercortisolism?.includes('złamania') && (
          <>
            <Grid item xs={12} mt={2}>
              <Input
                InputLabelProps={{ shrink: true }}
                required={true}
                fullWidth={true}
                name={'descriptionOfFractures'}
                label={'Opis złamań'}
                type={'text'}
                rows={5}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.descriptionOfFractures}
                error={Boolean(formik.errors.descriptionOfFractures)}
                multiline={true}
              />
            </Grid>
            <Grid item xs={12} mb={1}>
              <Typography variant={'caption'} mt={1} color={'#ED6C02'}>
                Proszę doprecyzować, np. złamanie trzonu L3, złamanie szyjki kości udowej lewej
              </Typography>
            </Grid>
          </>
        )}
        {formik.values.complicationsOfHypercortisolism?.includes('zaburzenia gospodarki węglowodanowej') && (
          <>
            <Grid xs={12} item mt={2}>
              <Label>Zaburzenia gospodarki węglowodanowej:</Label>
            </Grid>
            <Grid item xs={12} mt={1}>
              <CheckboxesGroup
                name={'carbohydrateMetabolismDisorders'}
                elements={carbohydrateMetabolismDisordersCheckboxesConfig}
                onChange={handleChangeArray}
                values={formik.values.carbohydrateMetabolismDisorders}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} mb={1}>
          <Typography variant={'h6'} mt={4} mb={2}>
            Choroby współistniejące
          </Typography>
        </Grid>
        {formik.values.comorbidities &&
          formik.values.comorbidities.map((el, index) => (
            <Grid item xs={12} mb={2} display={'flex'} gap={2} key={index} alignItems={'center'}>
              <Typography color={'text-primary'} variant={'body1'}>
                {index + 1}
              </Typography>
              <Input
                InputLabelProps={{ shrink: true }}
                fullWidth={true}
                name={`comorbidities[${index}]`}
                label={'Choroba'}
                type={'text'}
                onChange={e => {
                  formik.setFieldValue(`comorbidities[${index}]`, e.target.value);
                }}
                error={
                  Array.isArray(formik.touched.comorbidities) &&
                  formik.touched.comorbidities[index] &&
                  Array.isArray(formik.errors.comorbidities) &&
                  Boolean(formik.errors.comorbidities[index])
                }
                onBlur={formik.handleBlur}
                value={formik.values.comorbidities[index]}
              />
              <CloseIcon sx={{ cursor: 'pointer' }} color={'primary'} onClick={() => handleDeleteComorbidity(index)} />
            </Grid>
          ))}
        <Button
          sx={{ marginTop: 1 }}
          startIcon={<Add />}
          onClick={addComorbidity}
          size={'large'}
          color={'primary'}
          variant={'contained'}
        >
          Dodaj
        </Button>
        {isCushing && (
          <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} mt={2}>
            <Typography variant="caption" color={'rgba(0,0,0,0.6)'}>
              PL-IST-0068
            </Typography>
          </Grid>
        )}
        <ButtonsActions handleDraftSave={handleDraftSave} handleSave={handleSave} />
      </Grid>
    </form>
  );
});
